import type { AppFile, OrganizationBranding } from "../database/zod";






export enum IframeToChildMessageType {
  // Sent from the child to the parent when it's ready to receive messages.
  IFRAME_READY = "iframe-ready",
  // Sent from the parent to the child with initial configuration data (user, organization, plan info).
  LOAD_INITIAL_DATA = "load-initial-data",
  // Sent from the parent to the child to refresh the logo list when new logos are uploaded.
  REFRESH_LOGOS = "refresh-logos",
  // Sent from the parent to the child with video data for shorts
  ADD_SCENE = "add:scene",
  // Sent from the parent to the child with video data for shorts
  ADD_SHORT_VIDEO_SCENE = "add:short-video-scene",
  // Child requests data from parent
  REQUEST_DATA = "request-data",
  // Response from parent to child for PROGRESS_REQUEST
  PROGRESS_RESPONSE = "progress-response",
  // Response from parent to child for APP_FILES_REQUEST
  APP_FILES_RESPONSE = "app-files-response",
  // Sent from parent to child to open tour
  OPEN_TOUR = "open-tour",
  // Generic API response from parent to child
  API_RESPONSE = "api-response",
}

export enum IframeToParentMessageType {
  // Sent from the child to the parent when the user performs a save action.
  EDITOR_SAVE = "editor-save",
  // Response from child to parent for LOAD_INITIAL_DATA
  INITIAL_DATA_RESPONSE = "initial-data-response",
  // Response from parent to child for REQUEST_DATA
  REQUEST_DATA_RESPONSE = "request-data-response",
  // Child requests data from parent
  EXPORT_MP4_REQUEST = "export-mp4-request",
  // Response from parent to child for EXPORT_MP4_REQUEST
  EXPORT_MP4_RESPONSE = "export-mp4-response",
  // Child requests progress update from parent
  PROGRESS_REQUEST = "progress-request",
  // Child requests app files from parent
  APP_FILES_REQUEST = "app-files-request",
  // Child requests to open file manager modal
  OPEN_FILE_MANAGER = "open-file-manager",
  // Child requests to navigate to home page
  GOTO_HOME = "goto-home",
  // Sent from child to parent when tour is completed
  TOUR_OPENED = "tour-opened",
  // Generic API request from child to parent
  API_REQUEST = "api-request",
}

// Payload types for specific messages
export interface LoadInitialDataPayload {
  user: any; // Complete user object
  organization: any | null; // Complete organization object
  activePlan: {
    id: string;
    isFree?: boolean;
    trialPeriodDays?: number;
    credit?: number | null;
    resolution?: {
      width: number;
      height: number;
    } | null;
    status?: string | null;
    purchaseId?: string;
    pricePerProcessedMinute: number;
    storage?: number;
    maxFileUpload?: number;
    maxOrganizations?: number;
    maxMembersPerOrganization?: number;
    canResize: boolean;
    maxVideoDuration: number;
    /** From `config.payments.plans[activePlan.id]` — B-roll allowed for this plan. */
    bRollEnabled?: boolean;
    socialPostingEnabled?: boolean;
  };
  renderConfig?: {
    renderPricePerMinute: number;
    minimumRenderPrice: number;
  };
  brands?: OrganizationBranding[]; // Brands attached to the active organization
  appFiles?: AppFile[]; // App files attached to the active organization
}

export interface OpenFileManagerPayload {
  activeTab?: "upload" | "files";
  allowedTypes?: string[];
  defaultTags?: string[];
  title?: string;
  requireConfirmation?: boolean;
}

export interface ApiRequestPayload {
  endpoint: string; // e.g., "/organizations/brandings" or "/organizations/brandings/:id"
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  params?: Record<string, string>; // URL path parameters (e.g., { id: "123" })
  query?: Record<string, string | string[]>; // Query parameters
  body?: any; // Request body for POST/PUT/PATCH
  headers?: Record<string, string>; // Additional headers
}

export interface ApiResponsePayload {
  data?: any;
  error?: string;
  status?: number;
}

// A generic interface for all messages to ensure they follow the standard structure.
export interface IframeMessage<T> {
  type: IframeToChildMessageType | IframeToParentMessageType;
  payload: T;
  requestId?: string; // Optional requestId for request-response communication
} 